import { STATUS_COLOR } from '@/components/Affectations/affectation.utils';
import DecisionConfirmContent from '@/components/Affectations/DecisionConfirmContent';
import {
  useNotifyUserAffectationsMutation,
  useUpdateAffectationsMutation,
} from '@/features/api/endpoints/affectationEndpoints';
import type { Affectation } from '@/models/types';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { useTranslate } from '@tolgee/react';
import { useState } from 'react';

export type Decision = 'approve' | 'reject';

export type DecisionScope = 'one' | 'all';

export const DECISION_STATUS: Record<Decision, Affectation['status']> = {
  approve: 'published',
  reject: 'archived',
};

/** La couleur d'une décision est celle du statut qu'elle applique. */
export const decisionColor = (decision: Decision): string =>
  STATUS_COLOR[DECISION_STATUS[decision]];

type DecisionKeys = {
  action: string;
  confirmTitle: string;
  confirmMessage: string;
  success: string;
};

// Clés Tolgee écrites en entier pour rester détectables par recherche et extraction.
export const DECISION_KEYS: Record<Decision, Record<DecisionScope, DecisionKeys>> = {
  approve: {
    one: {
      action: 'admin.affectations.approve',
      confirmTitle: 'admin.affectations.approve.confirm.title',
      confirmMessage: 'admin.affectations.approve.confirm.message',
      success: 'admin.affectations.approve.success',
    },
    all: {
      action: 'admin.affectations.approveAll',
      confirmTitle: 'admin.affectations.approveAll.confirm.title',
      confirmMessage: 'admin.affectations.approveAll.confirm.message',
      success: 'admin.affectations.approveAll.success',
    },
  },
  reject: {
    one: {
      action: 'admin.affectations.reject',
      confirmTitle: 'admin.affectations.reject.confirm.title',
      confirmMessage: 'admin.affectations.reject.confirm.message',
      success: 'admin.affectations.reject.success',
    },
    all: {
      action: 'admin.affectations.rejectAll',
      confirmTitle: 'admin.affectations.rejectAll.confirm.title',
      confirmMessage: 'admin.affectations.rejectAll.confirm.message',
      success: 'admin.affectations.rejectAll.success',
    },
  },
};

/**
 * Décision de l'administrateur sur les affectations d'un utilisateur : confirmation,
 * changement de statut en une seule requête, puis information optionnelle par e-mail.
 */
const useAffectationDecision = (userId: string) => {
  const { t } = useTranslate();
  const [updatingIds, setUpdatingIds] = useState<number[]>([]);
  const [updateAffectations] = useUpdateAffectationsMutation();
  const [notifyUserAffectations] = useNotifyUserAffectationsMutation();

  const isUpdating = (affectation: Affectation) => updatingIds.includes(affectation.id);

  /** Confirme puis applique la décision à une affectation seule, ou à plusieurs. */
  const onDecisionClick = (targets: Affectation[], decision: Decision, scope: DecisionScope) => {
    const keys = DECISION_KEYS[decision][scope];
    const count = targets.length;
    const ids = targets.map(affectation => affectation.id);
    // Un seul mail par action : coché par défaut pour un traitement groupé, pas pour une
    // ligne seule (l'administrateur le coche à la dernière décision).
    const defaultNotify = scope === 'all';
    let notify = defaultNotify;

    modals.openConfirmModal({
      title: t(keys.confirmTitle),
      children: (
        <DecisionConfirmContent
          message={t(keys.confirmMessage, { count })}
          defaultNotify={defaultNotify}
          onNotifyChange={value => {
            notify = value;
          }}
        />
      ),
      labels: {
        confirm: t(keys.action),
        cancel: t('common.cancel'),
      },
      confirmProps: { color: decisionColor(decision) },
      centered: true,
      onConfirm: async () => {
        setUpdatingIds(current => [...current, ...ids]);
        try {
          await updateAffectations({
            ids,
            body: { status: DECISION_STATUS[decision] },
          }).unwrap();
          notifications.show({
            color: 'green',
            message: t(keys.success, { count }),
          });
          if (notify) {
            try {
              await notifyUserAffectations({ userId }).unwrap();
              notifications.show({
                color: 'green',
                message: t('admin.affectations.notify.success'),
              });
            } catch {
              notifications.show({
                color: 'red',
                message: t('admin.affectations.notify.error'),
              });
            }
          }
        } catch {
          notifications.show({
            color: 'red',
            message: t('admin.affectations.update.error'),
          });
        } finally {
          setUpdatingIds(current => current.filter(id => !ids.includes(id)));
        }
      },
    });
  };

  return { onDecisionClick, isUpdating };
};

export default useAffectationDecision;
